/* Order Tracker (WI-31 lookup, WI-32 documents, WI-33 order view, WI-37 email
   preference) on /pages/track-order. Markup: snippets/dha-order-tracker.liquid. Contract:
   OrderHub docs/public-tracking-api.md (POST {access_code} -> status,
   order {number, total, products, ...}, shared files; POST /api/file with
   {access_code, attachment_id} downloads one). WI-36: the access code is the
   only lookup credential; the number-plus-email request is no longer sent.

   WI-34 adds the remaining balance (beside the total) and shipping details.
   WI-37 adds the optional "Email me order status updates" checkbox: after a
   lookup, POST /api/notifications {access_code} reads the saved preference and
   {access_code, subscribed} saves it. The saved preference is always shown
   (WI-38); the box is disabled while the API says updates are not available.
   It controls consent only and never takes an address.
   WI-35 hardening: requests go only to the approved OrderHub origin and paths
   (redirects rejected), and a download is saved only when its listed size,
   a valid SHA-256 header and the bytes' hash all agree.

   Two views in one container: the lookup, replaced in place by the order view
   after a successful lookup; "Track another order" restores the lookup.

   Privacy rules this file enforces:
   - the access code lives only in the input element (emptied on success),
     one in-memory object for the displayed lookup (used to re-authorise
     downloads) and request bodies: never in a URL, cookie, local/session
     storage, console output, or any analytics/dataLayer call;
   - responses are rendered with textContent only, and only the fields the
     contract promises are ever shown. */
(function () {
  'use strict';

  /* The only destination credentials are ever sent to: the approved OrderHub
     public origin and its two documented paths (OrderHub
     docs/public-tracking-api.md). The block's "Tracking endpoint URL" setting
     is only checked against this; it never selects the destination, and the
     download URL is a constant, not derived from it. Moving OrderHub to a
     custom domain is a code change here, on purpose. */
  var ORDERHUB_ORIGIN = 'https://dha-orderhub-public.dha-orderhub.workers.dev';
  var TRACK_PATH = '/api/track';
  var FILE_PATH = '/api/file';
  var NOTIFY_PATH = '/api/notifications';

  var ORDER_RE = /^DHA-\d{5,12}$/; // validates the order number the API returns
  // Access code (contract: 12 Crockford base32 characters, forgiving presentation):
  // 0-9 and A-Z without I, L, O, U after normalization. Used only to give instant
  // help; the server stays authoritative and always receives what was typed.
  var CODE_RE = /^[0-9A-HJKMNP-TV-Z]{12}$/;
  var CODE_SEPARATORS_RE = /[\s\-\u2010-\u2015\u2212\ufe58\ufe63\uff0d]/g;
  var CODE_MAX_TYPED = 64;
  var DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
  var AMOUNT_RE = /^(\d{1,15})\.(\d{2})$/;
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'];
  var TIMEOUT_MS = 15000;
  var DOWNLOAD_TIMEOUT_MS = 120000; // files can be up to 25 MB
  var DEFAULT_LOCK_SECONDS = 60; // the contract's Retry-After is always 60
  var WHEELS_PER_SET = 4;
  var ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  // control characters and bidi overrides, which can disguise a file extension
  var SHA256_RE = /^[0-9a-f]{64}$/i;
  var UNSAFE_RE = /[\u0000-\u001f\u007f-\u009f​-‏‪-‮⁦-⁩﻿]/g;

  var NOTIFY_MESSAGES = {
    saving: 'Saving…',
    on: 'Saved. Email updates are on for this order.',
    off: 'Saved. Email updates are off for this order.',
    paused: 'Email updates are currently paused. Your saved preference is unchanged.',
    unreadable: 'Email preferences are temporarily unavailable.',
    unconfirmed: "We couldn't confirm your choice, so your last saved setting is shown. Please try again.",
    notfound: "We couldn't save your choice, so your last saved setting is shown. Please contact us if this keeps happening.",
    limited: 'Too many changes in a short time. Your last saved setting is shown. Please wait a minute and try again.'
  };

  var DOC_MESSAGES = {
    gone: 'This file is no longer available here. Contact us and we can send you a copy.',
    error: 'The download did not complete. Nothing was saved. Please try again in a moment.',
    timeout: 'The download is taking longer than expected. Nothing was saved. Please try again.',
    limited: 'Too many download requests. Please wait a minute and try again.',
    integrity: 'This file could not be verified, so nothing was saved. Please try again, or contact us and we can send you a copy.'
  };

  var LOOKUP_MESSAGES = {
    notfound: { kind: 'info', title: "We couldn't find an order with that code.", body: 'Check the access code DHA gave you and try again, or contact us.' },
    invalid: { kind: 'error', title: 'Please check the access code.', body: 'It has 12 letters and numbers, like XXXX-XXXX-XXXX.' },
    error: { kind: 'error', title: "We can't check orders right now.", body: 'Please try again in a few minutes, or contact us and we will look it up for you.' },
    timeout: { kind: 'error', title: 'This is taking longer than expected.', body: 'Please try again in a moment, or contact us and we will look it up for you.' },
    limited: { kind: 'limited', title: 'Please wait before trying again.', body: 'There have been several lookups in a short time. You can try again in about a minute.' },
    checking: { kind: 'checking', title: 'Checking your order…', body: '' }
  };

  /* "2026-10-02" -> "2 October 2026", computed from the string parts so the
     API's UTC calendar date is never shifted by the visitor's timezone. */
  function formatDate(iso) {
    var m = typeof iso === 'string' ? DATE_RE.exec(iso) : null;
    if (!m) return null;
    var month = Number(m[2]);
    var day = Number(m[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return { text: day + ' ' + MONTHS[month - 1] + ' ' + Number(m[1]), iso: iso };
  }

  /* {currency:'USD', amount:'10440.00'} -> '$10,440.00', from the exact
     string (never a float). null/undefined/malformed -> null ("not listed").
     A genuine "0.00" is a real zero and is shown as $0.00. */
  function formatTotal(total) {
    if (!total || total.currency !== 'USD' || typeof total.amount !== 'string') return null;
    var m = AMOUNT_RE.exec(total.amount);
    if (!m) return null;
    return '$' + m[1].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + m[2];
  }

  /* Remaining balance: null/malformed -> hidden, "0.00" -> a real zero,
     anything else -> the exact amount. Never parsed as a float. */
  function parseBalance(b) {
    var text = formatTotal(b);
    if (!text) return { kind: 'none' };
    return /^0+\.00$/.test(b.amount) ? { kind: 'zero' } : { kind: 'positive', text: text };
  }

  /* Only absolute http(s) links without embedded credentials are ever
     clickable; anything else is dropped. */
  function safeUrl(value) {
    var raw = cleanText(value, 2000);
    if (!raw) return null;
    try {
      var u = new URL(raw);
      if ((u.protocol !== 'http:' && u.protocol !== 'https:') || u.username || u.password) return null;
      return u.href;
    } catch (e) { return null; }
  }

  /* order.shipping -> {carrier, number, url}; unsupplied fields are null. */
  function parseShipping(sh) {
    var o = sh && typeof sh === 'object' ? sh : {};
    return { carrier: cleanText(o.carrier, 120) || null, number: cleanText(o.tracking_number, 160) || null, url: safeUrl(o.tracking_url) };
  }

  /* '' when the typed text could be a code, otherwise 'empty' or 'malformed'.
     Mirrors the documented normalization (trim, ignore case, drop spaces,
     hyphens and Unicode dashes, O->0, I/L->1, then 12 characters from the
     alphabet). Advisory: it never changes what is sent. */
  function codeProblem(typed) {
    var t = typed.trim();
    if (!t) return 'empty';
    if (t.length > CODE_MAX_TYPED) return 'malformed';
    var n = t.toUpperCase().replace(CODE_SEPARATORS_RE, '').replace(/O/g, '0').replace(/[IL]/g, '1');
    return CODE_RE.test(n) ? '' : 'malformed';
  }

  /* The configured lookup URL must be exactly the approved endpoint (https,
     approved origin, /api/track, no port, credentials, query or fragment);
     anything else returns null and no request is ever made. */
  function approvedEndpoints(configured) {
    var u;
    try { u = new URL(String(configured || '').trim()); } catch (e) { return null; }
    if (u.href !== ORDERHUB_ORIGIN + TRACK_PATH) return null;
    return { track: ORDERHUB_ORIGIN + TRACK_PATH, file: ORDERHUB_ORIGIN + FILE_PATH, notify: ORDERHUB_ORIGIN + NOTIFY_PATH };
  }

  /* A response that was redirected, or that did not come from the URL we
     asked, is never trusted (requests also use redirect: 'error'). */
  function fromExpectedUrl(res, url) {
    return !res.redirected && res.type !== 'opaqueredirect' && (!res.url || res.url === url);
  }

  /* 48213 -> "47.1 KB" (binary units, one decimal from KB up). */
  function formatSize(bytes) {
    if (typeof bytes !== 'number' || !isFinite(bytes) || bytes < 0) return '';
    if (bytes < 1024) return Math.round(bytes) + ' B';
    var units = ['KB', 'MB', 'GB'];
    var value = bytes / 1024;
    var i = 0;
    while (value >= 1024 && i < units.length - 1) { value /= 1024; i += 1; }
    return value.toFixed(1) + ' ' + units[i];
  }

  function cleanText(text, max) {
    return typeof text === 'string' ? text.replace(UNSAFE_RE, '').trim().slice(0, max) : '';
  }

  /* File names are untrusted text: strip control/bidi characters, path parts
     and surrounding whitespace. Used for display and the saved file name. */
  function cleanName(name) {
    var n = typeof name === 'string' ? name.replace(UNSAFE_RE, '') : '';
    n = n.replace(/^.*[\\\/]/, '').trim().slice(0, 200);
    return n || 'Document';
  }

  /* data.files -> [{id, name, meta}]; malformed entries are dropped. */
  function parseFiles(list) {
    var out = [];
    if (!Array.isArray(list)) return out;
    for (var i = 0; i < list.length; i++) {
      var f = list[i];
      if (!f || typeof f.id !== 'string' || !ID_RE.test(f.id)) continue;
      // the listed size is what a download is verified against; no size, no file
      if (typeof f.size_bytes !== 'number' || f.size_bytes % 1 !== 0 || f.size_bytes < 1) continue;
      var name = cleanName(f.name);
      var ext = /\.([A-Za-z0-9]{1,5})$/.exec(name);
      var size = formatSize(f.size_bytes);
      var meta = (ext ? ext[1].toUpperCase() : '') + (ext && size ? ' · ' : '') + size;
      out.push({ id: f.id, name: name, meta: meta, size: size, bytes: f.size_bytes });
    }
    return out;
  }

  /* order.products -> [{name, finish, sets}]; invalid lines are dropped. */
  function parseProducts(list) {
    var out = [];
    if (!Array.isArray(list)) return out;
    for (var i = 0; i < list.length; i++) {
      var p = list[i];
      if (!p || typeof p !== 'object') continue;
      var name = cleanText(p.product, 120);
      var sets = p.sets;
      if (!name || typeof sets !== 'number' || sets % 1 !== 0 || sets < 1 || sets > 999) continue;
      out.push({ name: name, finish: cleanText(p.finish, 120) || null, sets: sets });
    }
    return out;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function init(root) {
    var ui = root.querySelector('[data-dha-trk-ui]');
    var orderView = root.querySelector('[data-dha-trk-view]');
    var codeInput = root.querySelector('[data-dha-trk-code]');
    var codeErr = root.querySelector('[data-dha-trk-code-err]');
    var button = root.querySelector('[data-dha-trk-submit]');
    var result = root.querySelector('[data-dha-trk-result]');
    var backBtn = root.querySelector('[data-dha-trk-back]');
    var numberEl = root.querySelector('[data-dha-trk-number]');
    var numberCopy = root.querySelector('[data-dha-trk-number-copy]');
    var statusEl = root.querySelector('[data-dha-trk-status]');
    var dateEl = root.querySelector('[data-dha-trk-date]');
    var totalEl = root.querySelector('[data-dha-trk-total]');
    var balanceEl = root.querySelector('[data-dha-trk-balance]');
    var shippingEl = root.querySelector('[data-dha-trk-shipping]');
    var shippingList = root.querySelector('[data-dha-trk-shipping-list]');
    var productsList = root.querySelector('[data-dha-trk-products]');
    var productsEmpty = root.querySelector('[data-dha-trk-products-empty]');
    var docsList = root.querySelector('[data-dha-trk-docs-list]');
    var docsEmpty = root.querySelector('[data-dha-trk-docs-empty]');
    var notifyEl = root.querySelector('[data-dha-trk-notify]');
    var notifyBox = root.querySelector('[data-dha-trk-notify-box]');
    var notifyMsg = root.querySelector('[data-dha-trk-notify-msg]');
    var notifyBody = root.querySelector('[data-dha-trk-notify-body]');
    if (!ui || !orderView || !codeInput || !codeErr || !button || !result ||
        !balanceEl || !shippingEl || !shippingList || !backBtn || !numberEl || !numberCopy || !statusEl || !dateEl || !totalEl || !productsList ||
        !productsEmpty || !docsList || !docsEmpty) return;

    var endpoints = approvedEndpoints(root.getAttribute('data-endpoint'));
    var seq = 0;          // identifies the current lookup; stale responses are dropped
    var controller = null;
    var timeoutTimer = null;
    var lockTimer = null;
    var loading = false;
    var locked = false;   // rate-limit cool-down: the button stays disabled
    var retryLabel = false; // after a temporary failure the button reads "Try again"

    /* Documents. `session` holds the credentials of the lookup being
       displayed, in memory only, so each download can be re-authorised; it is
       dropped the moment that lookup is invalidated or the customer leaves
       the order view. */
    var session = null;       // { seq, code }
    var dlController = null;
    var dlTimer = null;
    var dlBusy = false;
    var dlLocked = false;     // download rate-limit cool-down
    var dlLockTimer = null;
    var dlButtons = [];
    var dlToken = null;

    /* Email preference. Like `session`, it belongs to the displayed lookup and
       is dropped with it. `nToken` identifies the one read or save in flight;
       `nConfirmed` is the last state OrderHub reported, which the checkbox
       always returns to when a save is not confirmed. */
    var nToken = null;
    var nController = null;
    var nTimer = null;
    var nLockTimer = null;
    var nBusy = false;
    var nConfirmed = false;
    var nAvailable = false;   // OrderHub is currently accepting changes; false = paused (box shown, disabled)

    ui.hidden = false;
    orderView.hidden = true;

    function updateButton() {
      button.disabled = loading || locked;
      button.textContent = loading ? 'Checking…' : (retryLabel ? 'Try again' : 'Check status');
      if (loading) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }

    function clearResult() {
      result.textContent = '';
      result.className = 'dha-trk__result';
    }

    function showMessage(spec) {
      result.textContent = '';
      result.className = 'dha-trk__result dha-trk__result--' + spec.kind;
      var box = el('div', 'dha-trk__msg');
      box.appendChild(el('p', 'dha-trk__msg-title', spec.title));
      if (spec.body) box.appendChild(el('p', 'dha-trk__msg-body', spec.body));
      if (spec.kind === 'checking') {
        var sk = el('div', 'dha-trk__skeleton');
        sk.setAttribute('aria-hidden', 'true');
        sk.appendChild(el('span', 'dha-trk__skeleton-bar dha-trk__skeleton-bar--s'));
        sk.appendChild(el('span', 'dha-trk__skeleton-bar dha-trk__skeleton-bar--m'));
        sk.appendChild(el('span', 'dha-trk__skeleton-bar dha-trk__skeleton-bar--l'));
        box.appendChild(sk);
      }
      result.appendChild(box);
    }

    function setFieldError(input, errNode, text) {
      if (text) {
        errNode.textContent = text;
        errNode.hidden = false;
        input.setAttribute('aria-invalid', 'true');
      } else {
        errNode.textContent = '';
        errNode.hidden = true;
        input.removeAttribute('aria-invalid');
      }
    }

    /* ---- Documents --------------------------------------------------- */

    function setDocMessage(entry, kind, text) {
      for (var i = 0; i < dlButtons.length; i++) {
        dlButtons[i].msg.textContent = '';
        dlButtons[i].msg.className = 'dha-trk__doc-msg';
      }
      if (entry && text) {
        entry.msg.textContent = text;
        entry.msg.className = 'dha-trk__doc-msg dha-trk__doc-msg--' + kind;
      }
    }

    function updateDlButtons() {
      for (var i = 0; i < dlButtons.length; i++) {
        var b = dlButtons[i];
        if (b.gone) continue;
        var busyHere = dlBusy && b.active;
        var off = dlLocked || (dlBusy && !b.active);
        if (off) b.node.setAttribute('aria-disabled', 'true'); else b.node.removeAttribute('aria-disabled');
        if (busyHere) b.node.setAttribute('aria-busy', 'true'); else b.node.removeAttribute('aria-busy');
        b.action.textContent = busyHere ? 'Downloading…' : 'Download';
      }
    }

    /* ---- Email preference --------------------------------------------- */

    function setNotifyMessage(kind, text) {
      notifyMsg.textContent = text || '';
      notifyMsg.className = 'dha-trk__notify-msg' + (text && kind ? ' dha-trk__notify-msg--' + kind : '');
    }

    /* Hide the control and forget the order's preference, cancelling any
       read or save in flight so a late response is a no-op. */
    function clearNotify() {
      nToken = null;
      if (nController) { try { nController.abort(); } catch (e) { /* ignore */ } }
      nController = null;
      if (nTimer) { clearTimeout(nTimer); nTimer = null; }
      if (nLockTimer) { clearTimeout(nLockTimer); nLockTimer = null; }
      nBusy = false;
      nConfirmed = false;
      nAvailable = false;
      if (!notifyEl) return;
      notifyEl.hidden = true;
      notifyEl.classList.remove('dha-trk__notify--paused');
      notifyBody.hidden = false;
      notifyBox.checked = false;
      notifyBox.disabled = false;
      notifyBox.removeAttribute('aria-busy');
      setNotifyMessage(null, '');
    }

    /* One request to /api/notifications with the displayed lookup's code.
       Calls done(outcome) once, only if this lookup and request are still the
       current ones (a late response from an earlier order is dropped). */
    function notifyRequest(body, token, mine, done) {
      var finished = false;
      function finish(outcome) {
        if (finished) return;
        finished = true;
        if (mine !== seq || nToken !== token) return;
        nController = null;
        if (nTimer) { clearTimeout(nTimer); nTimer = null; }
        done(outcome);
      }
      if (typeof fetch !== 'function' || !endpoints) {
        finish({ kind: 'error' });
        return;
      }
      var request = { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        mode: 'cors', credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer', redirect: 'error' };
      if (typeof AbortController === 'function') {
        nController = new AbortController();
        request.signal = nController.signal;
      }
      nTimer = setTimeout(function () {
        if (nController) { try { nController.abort(); } catch (e) { /* ignore */ } }
        finish({ kind: 'error' });
      }, TIMEOUT_MS);
      fetch(endpoints.notify, request).then(function (res) {
        if (!fromExpectedUrl(res, endpoints.notify)) return { kind: 'error' };
        return res.text().then(function (text) {
          var data = null;
          try { data = JSON.parse(text); } catch (e) { data = null; }
          var n = data && data.ok === true ? data.notifications : null;
          if (res.status === 200 && n && typeof n.available === 'boolean' && typeof n.subscribed === 'boolean') {
            return { kind: 'ok', available: n.available, subscribed: n.subscribed };
          }
          if (res.status === 404) return { kind: 'notfound' };
          if (res.status === 429) {
            var s = parseInt(res.headers.get('Retry-After'), 10);
            return { kind: 'limited', seconds: Math.min(s >= 1 ? s : DEFAULT_LOCK_SECONDS, 300) };
          }
          return { kind: 'error' };
        });
      }).then(finish, function () {
        finish({ kind: 'error' });
      });
    }

    /* Draw the control from the last server-confirmed state: the saved value
       is always shown; when sending is paused the box is disabled and greyed
       out with an explanation, and changes are never sent. */
    function showNotify() {
      notifyEl.hidden = false;
      notifyBody.hidden = false;
      notifyBox.checked = nConfirmed;
      notifyBox.disabled = !nAvailable;
      notifyEl.classList.toggle('dha-trk__notify--paused', !nAvailable);
    }

    /* Read the saved preference and always show it. If it cannot be read,
       no state is invented: only a quiet message appears, and the order
       details and downloads are never touched. */
    function loadNotify(code, mine) {
      if (!notifyEl) return;
      var token = {};
      nToken = token;
      notifyRequest({ access_code: code }, token, mine, function (outcome) {
        if (outcome.kind !== 'ok') {
          notifyEl.hidden = false;
          notifyBody.hidden = true;
          setNotifyMessage('quiet', NOTIFY_MESSAGES.unreadable);
          return;
        }
        nConfirmed = outcome.subscribed;
        nAvailable = outcome.available;
        showNotify();
        setNotifyMessage(nAvailable ? null : 'paused', nAvailable ? '' : NOTIFY_MESSAGES.paused);
      });
    }

    /* Save the box's new state; the response, not the click, decides what is
       shown afterwards. Anything unconfirmed restores the last confirmed state. */
    function saveNotify() {
      if (nBusy || !nAvailable || !session || session.seq !== seq || notifyEl.hidden) return;
      var mine = seq;
      var desired = notifyBox.checked;
      var token = {};
      var hadFocus = document.activeElement === notifyBox;
      nToken = token;
      nBusy = true;
      notifyBox.disabled = true;
      notifyBox.setAttribute('aria-busy', 'true');
      setNotifyMessage('saving', NOTIFY_MESSAGES.saving);
      notifyRequest({ access_code: session.code, subscribed: desired }, token, mine, function (outcome) {
        nBusy = false;
        notifyBox.removeAttribute('aria-busy');
        if (outcome.kind === 'ok') {
          nConfirmed = outcome.subscribed;
          nAvailable = outcome.available;
          showNotify();
          if (!nAvailable) { // sending was paused meanwhile: show the server-confirmed preference, disabled
            setNotifyMessage('paused', NOTIFY_MESSAGES.paused);
            return;
          }
          setNotifyMessage('ok', nConfirmed ? NOTIFY_MESSAGES.on : NOTIFY_MESSAGES.off);
        } else {
          showNotify();
          if (outcome.kind === 'limited') {
            notifyBox.disabled = true;
            setNotifyMessage('error', NOTIFY_MESSAGES.limited);
            nLockTimer = setTimeout(function () {
              nLockTimer = null;
              if (mine !== seq) return;
              notifyBox.disabled = !nAvailable;
              setNotifyMessage(null, '');
            }, outcome.seconds * 1000);
            return;
          }
          setNotifyMessage('error', outcome.kind === 'notfound' ? NOTIFY_MESSAGES.notfound : NOTIFY_MESSAGES.unconfirmed);
        }
        if (hadFocus) notifyBox.focus();
      });
    }

    /* Forget everything document-related: list, credentials, any in-flight
       download and its timers, and the displayed order's email preference. */
    function clearDocs() {
      clearNotify();
      session = null;
      if (dlController) { try { dlController.abort(); } catch (e) { /* ignore */ } }
      dlController = null;
      if (dlTimer) { clearTimeout(dlTimer); dlTimer = null; }
      if (dlLockTimer) { clearTimeout(dlLockTimer); dlLockTimer = null; }
      dlBusy = false;
      dlLocked = false;
      dlToken = null;
      dlButtons = [];
      docsList.textContent = '';
      docsEmpty.hidden = true;
    }

    function saveBlob(blob, name) {
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.rel = 'noopener';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(function () { URL.revokeObjectURL(url); }, 10000);
    }

    /* A download is saved only if every check passes: the listed size
       matches, a valid X-Content-SHA256 header is present and matches the
       SHA-256 of the bytes. A missing or malformed header, missing crypto
       support or any mismatch fails closed (resolves false). */
    function verifyDownload(blob, expectedHash, listedBytes) {
      if (!blob || blob.size !== listedBytes || !expectedHash || !SHA256_RE.test(expectedHash) ||
          !window.crypto || !window.crypto.subtle || typeof blob.arrayBuffer !== 'function') {
        return Promise.resolve(false);
      }
      return blob.arrayBuffer().then(function (buf) {
        return window.crypto.subtle.digest('SHA-256', buf);
      }).then(function (digest) {
        var hex = '';
        var bytes = new Uint8Array(digest);
        for (var i = 0; i < bytes.length; i++) hex += (bytes[i] < 16 ? '0' : '') + bytes[i].toString(16);
        return hex === expectedHash.toLowerCase();
      }, function () { return false; });
    }

    function startDlLock(seconds, entry) {
      dlLocked = true;
      if (dlLockTimer) clearTimeout(dlLockTimer);
      var mine = seq;
      dlLockTimer = setTimeout(function () {
        dlLockTimer = null;
        if (mine !== seq) return;
        dlLocked = false;
        if (entry.msg.classList.contains('dha-trk__doc-msg--limited')) setDocMessage(null);
        updateDlButtons();
      }, seconds * 1000);
      setDocMessage(entry, 'limited', DOC_MESSAGES.limited);
      updateDlButtons();
    }

    function download(entry, file) {
      if (dlBusy || dlLocked || !session || session.seq !== seq) return;
      if (!endpoints || typeof fetch !== 'function') {
        setDocMessage(entry, 'error', DOC_MESSAGES.error);
        return;
      }
      var mine = seq;
      var creds = session;
      var token = {};
      dlBusy = true;
      entry.active = true;
      dlToken = token;
      setDocMessage(null);
      updateDlButtons();

      var finished = false;
      var timedOut = false;
      function finish(outcome) {
        if (finished) return;
        finished = true;
        if (mine !== seq || dlToken !== token) return; // lookup changed: drop silently
        dlToken = null;
        dlBusy = false;
        entry.active = false;
        dlController = null;
        if (dlTimer) { clearTimeout(dlTimer); dlTimer = null; }
        if (outcome.kind === 'ok') {
          saveBlob(outcome.blob, file.name);
          setDocMessage(entry, 'ok', 'Downloaded.');
        } else if (outcome.kind === 'gone') {
          entry.gone = true;
          entry.node.setAttribute('aria-disabled', 'true');
          entry.node.setAttribute('aria-label', file.name + ' is no longer available');
          entry.node.removeAttribute('aria-busy');
          entry.action.textContent = 'Unavailable';
          entry.row.classList.add('dha-trk__doc--gone');
          setDocMessage(entry, 'error', DOC_MESSAGES.gone);
        } else if (outcome.kind === 'limited') {
          startDlLock(outcome.seconds, entry);
          return;
        } else {
          setDocMessage(entry, 'error', DOC_MESSAGES[outcome.kind] || DOC_MESSAGES.error);
        }
        updateDlButtons();
      }

      var request = { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_code: creds.code, attachment_id: file.id }),
        mode: 'cors', credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer', redirect: 'error' };
      if (typeof AbortController === 'function') {
        dlController = new AbortController();
        request.signal = dlController.signal;
      }
      dlTimer = setTimeout(function () {
        timedOut = true;
        if (dlController) { try { dlController.abort(); } catch (e) { /* ignore */ } }
        finish({ kind: 'timeout' });
      }, DOWNLOAD_TIMEOUT_MS);

      fetch(endpoints.file, request).then(function (res) {
        if (!fromExpectedUrl(res, endpoints.file)) return { kind: 'error' };
        if (res.status === 404) return { kind: 'gone' };
        if (res.status === 429) {
          var s = parseInt(res.headers.get('Retry-After'), 10);
          return { kind: 'limited', seconds: Math.min(s >= 1 ? s : DEFAULT_LOCK_SECONDS, 300) };
        }
        var type = (res.headers.get('Content-Type') || '').toLowerCase();
        // Only a 200 octet-stream is a document; JSON/HTML/text bodies are error pages.
        if (res.status !== 200 || type.indexOf('application/octet-stream') !== 0) return { kind: 'error' };
        var expected = res.headers.get('X-Content-SHA256');
        return res.blob().then(function (blob) {
          return verifyDownload(blob, expected, file.bytes).then(function (ok) {
            return ok ? { kind: 'ok', blob: blob } : { kind: 'integrity' };
          });
        });
      }).then(function (outcome) {
        finish(outcome);
      }).catch(function () {
        finish({ kind: timedOut ? 'timeout' : 'error' });
      });
    }

    function renderDocs(files) {
      docsList.textContent = '';
      dlButtons = [];
      docsEmpty.hidden = files.length > 0;
      docsList.hidden = files.length === 0;
      files.forEach(function (file) {
        var row = el('li', 'dha-trk__doc');
        var btn = el('button', 'dha-trk__doc-btn');
        btn.type = 'button';
        btn.setAttribute('aria-label', 'Download ' + file.name + (file.size ? ' (' + file.size + ')' : ''));
        var info = el('span', 'dha-trk__doc-info');
        info.appendChild(el('span', 'dha-trk__doc-name', file.name));
        if (file.meta) info.appendChild(el('span', 'dha-trk__doc-meta', file.meta));
        var action = el('span', 'dha-trk__doc-action', 'Download');
        action.setAttribute('aria-hidden', 'true');
        btn.appendChild(info);
        btn.appendChild(action);
        var msg = el('p', 'dha-trk__doc-msg');
        msg.setAttribute('role', 'status');
        msg.setAttribute('aria-live', 'polite');
        var entry = { node: btn, row: row, action: action, msg: msg, active: false, gone: false };
        btn.addEventListener('click', function () {
          if (btn.getAttribute('aria-disabled') === 'true') return;
          download(entry, file);
        });
        row.appendChild(btn);
        row.appendChild(msg);
        docsList.appendChild(row);
        dlButtons.push(entry);
      });
    }

    /* ---- Order view -------------------------------------------------- */

    function renderProducts(products) {
      productsList.textContent = '';
      productsEmpty.hidden = products.length > 0;
      productsList.hidden = products.length === 0;
      products.forEach(function (p) {
        var li = el('li', 'dha-trk__product');
        var left = el('div', 'dha-trk__product-main');
        left.appendChild(el('p', 'dha-trk__product-name', p.name));
        if (p.finish) left.appendChild(el('p', 'dha-trk__product-finish', p.finish));
        var right = el('div', 'dha-trk__product-qty');
        right.appendChild(el('p', 'dha-trk__product-sets', p.sets + (p.sets === 1 ? ' set' : ' sets')));
        right.appendChild(el('p', 'dha-trk__product-wheels', p.sets * WHEELS_PER_SET + ' wheels'));
        li.appendChild(left);
        li.appendChild(right);
        productsList.appendChild(li);
      });
    }

    function renderBalance(b) {
      balanceEl.textContent = '';
      balanceEl.hidden = b.kind === 'none';
      if (b.kind === 'zero') {
        balanceEl.textContent = 'No remaining balance.';
      } else if (b.kind === 'positive') {
        balanceEl.appendChild(el('span', 'dha-trk__balance-label', 'Remaining balance'));
        balanceEl.appendChild(el('span', 'dha-trk__balance-amount', b.text + ' USD'));
      }
    }

    function renderShipping(sh) {
      shippingList.textContent = '';
      shippingEl.hidden = !(sh && (sh.carrier || sh.number || sh.url));
      if (shippingEl.hidden) return;
      function row(label, node) {
        var d = el('div', 'dha-trk__ship-row');
        d.appendChild(el('dt', null, label));
        var dd = el('dd');
        dd.appendChild(node);
        d.appendChild(dd);
        shippingList.appendChild(d);
      }
      if (sh.carrier) row('Carrier', document.createTextNode(sh.carrier));
      if (sh.number) row('Tracking number', document.createTextNode(sh.number));
      if (sh.url) {
        var a = el('a', 'dha-trk__ship-link', sh.url);
        a.href = sh.url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        row('Tracking link', a);
      }
    }

    function renderOrder(o) {
      numberEl.textContent = o.number;
      numberCopy.textContent = o.number;
      statusEl.textContent = o.label;
      dateEl.textContent = '';
      if (o.date) {
        var time = el('time', null, o.date.text);
        time.setAttribute('datetime', o.date.iso);
        dateEl.appendChild(time);
      } else {
        dateEl.textContent = '—';
      }
      totalEl.textContent = '';
      if (o.total) {
        totalEl.className = 'dha-trk__total';
        totalEl.appendChild(el('span', 'dha-trk__total-amount', o.total));
        totalEl.appendChild(el('span', 'dha-trk__total-cur', 'USD'));
      } else {
        totalEl.className = 'dha-trk__total dha-trk__total--unset';
        totalEl.textContent = 'Not listed yet';
      }
      renderBalance(o.balance);
      renderShipping(o.shipping);
      renderProducts(o.products);
      renderDocs(o.files);
    }

    /* Leave the order view: the previous order, its files and the in-memory
       credentials are dropped; the lookup returns empty. */
    function returnToLookup(focusField) {
      invalidate();
      codeInput.value = '';
      setFieldError(codeInput, codeErr, '');
      retryLabel = false;
      clearResult();
      renderProducts([]);
      renderBalance({ kind: 'none' });
      renderShipping(null);
      numberEl.textContent = '';
      numberCopy.textContent = '';
      statusEl.textContent = '';
      dateEl.textContent = '';
      totalEl.textContent = '';
      orderView.hidden = true;
      ui.hidden = false;
      updateButton();
      if (focusField) codeInput.focus();
    }

    /* Cancel whatever is in flight and make any late response a no-op. */
    function invalidate() {
      seq += 1;
      if (controller) { try { controller.abort(); } catch (e) { /* ignore */ } }
      controller = null;
      if (timeoutTimer) { clearTimeout(timeoutTimer); timeoutTimer = null; }
      loading = false;
      clearDocs(); // the previous lookup's files and credentials go with it
    }

    function startLock(seconds) {
      locked = true;
      if (lockTimer) clearTimeout(lockTimer);
      lockTimer = setTimeout(function () {
        locked = false;
        lockTimer = null;
        if (result.classList.contains('dha-trk__result--limited')) clearResult();
        updateButton();
      }, seconds * 1000);
      updateButton();
      showMessage(LOOKUP_MESSAGES.limited);
    }

    function settle(mine, outcome) {
      if (mine !== seq) return; // the customer edited, resubmitted or left: ignore
      loading = false;
      controller = null;
      if (timeoutTimer) { clearTimeout(timeoutTimer); timeoutTimer = null; }

      if (outcome.kind === 'success') {
        retryLabel = false;
        session = { seq: mine, code: outcome.code };
        codeInput.value = ''; // the code lives only in `session` now
        clearResult();
        renderOrder(outcome);
        ui.hidden = true;
        orderView.hidden = false;
        updateButton();
        numberEl.focus();
        loadNotify(outcome.code, mine);
        return;
      }
      retryLabel = outcome.kind === 'error' || outcome.kind === 'timeout';
      updateButton();
      if (outcome.kind === 'limited') {
        startLock(outcome.seconds);
      } else {
        showMessage(LOOKUP_MESSAGES[outcome.kind] || LOOKUP_MESSAGES.error);
      }
    }

    function classify(status, retryAfter, data, code) {
      if (status === 200 && data && data.ok === true && data.status &&
          typeof data.status.label === 'string' && data.status.label.trim() !== '') {
        var o = data.order && typeof data.order === 'object' ? data.order : {};
        // the order number comes only from the response; without a valid one the page cannot say which order this is
        var number = typeof o.number === 'string' && ORDER_RE.test(o.number.trim().toUpperCase())
          ? o.number.trim().toUpperCase() : null;
        if (!number) return { kind: 'error' };
        return { kind: 'success', label: data.status.label.trim(), date: formatDate(data.status.updated),
          number: number, total: formatTotal(o.total), balance: parseBalance(o.remaining_balance),
          shipping: parseShipping(o.shipping), products: parseProducts(o.products),
          files: parseFiles(data.files), code: code };
      }
      if (status === 404) return { kind: 'notfound' };
      if (status === 400) return { kind: 'invalid' };
      if (status === 429) {
        var s = parseInt(retryAfter, 10);
        if (!(s >= 1)) s = DEFAULT_LOCK_SECONDS; // Retry-After is not CORS-exposed; the contract says 60
        return { kind: 'limited', seconds: Math.min(s, 300) };
      }
      return { kind: 'error' }; // 405, 500, malformed 200, anything unexpected
    }

    function submit() {
      if (loading || locked) return;

      var code = codeInput.value.trim(); // what the customer typed; the server normalizes it
      var problem = codeProblem(code);
      setFieldError(codeInput, codeErr, problem === 'empty'
        ? 'Enter your access code.'
        : problem === 'malformed' ? 'Enter the 12-character access code DHA gave you, like XXXX-XXXX-XXXX.' : '');
      if (problem) {
        clearResult();
        codeInput.focus();
        return;
      }

      invalidate();
      var mine = seq;
      clearResult();
      retryLabel = false;
      loading = true;
      updateButton();
      showMessage(LOOKUP_MESSAGES.checking);

      var finished = false; // a lookup settles exactly once (response, timeout or failure)
      function finish(outcome) {
        if (finished) return;
        finished = true;
        settle(mine, outcome);
      }

      if (typeof fetch !== 'function' || !endpoints) {
        finish({ kind: 'error' }); // invalid configuration: nothing is transmitted
        return;
      }

      var timedOut = false;
      var request = { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_code: code }),
        mode: 'cors', credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer', redirect: 'error' };
      if (typeof AbortController === 'function') {
        controller = new AbortController();
        request.signal = controller.signal;
      }
      timeoutTimer = setTimeout(function () {
        timedOut = true;
        if (controller) { try { controller.abort(); } catch (e) { /* ignore */ } }
        finish({ kind: 'timeout' }); // also covers browsers without AbortController
      }, TIMEOUT_MS);

      fetch(endpoints.track, request).then(function (res) {
        if (!fromExpectedUrl(res, endpoints.track)) return { kind: 'error' };
        return res.text().then(function (text) {
          var data = null;
          try { data = JSON.parse(text); } catch (e) { data = null; }
          return classify(res.status, res.headers.get('Retry-After'), data, code);
        });
      }).then(function (outcome) {
        finish(outcome);
      }).catch(function () {
        finish({ kind: timedOut ? 'timeout' : 'error' });
      });
    }

    /* Editing either field invalidates any in-flight lookup and clears the
       displayed message, which no longer matches what is typed. A rate-limit
       notice stays until its cool-down ends. */
    function onEdit(input, errNode) {
      return function () {
        setFieldError(input, errNode, '');
        if (locked) { clearDocs(); return; }
        invalidate();
        clearResult();
        retryLabel = false;
        updateButton();
      };
    }

    codeInput.addEventListener('input', onEdit(codeInput, codeErr));
    button.addEventListener('click', submit);
    if (notifyBox) notifyBox.addEventListener('change', saveNotify);
    backBtn.addEventListener('click', function () { returnToLookup(true); });
    codeInput.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' || e.isComposing) return;
      e.preventDefault();
      submit();
    });
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      returnToLookup(false);
    });

    updateButton();
  }

  function boot() {
    var roots = document.querySelectorAll('[data-dha-trk]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
